import LocalStorageKey from '@enums/LocalStorageKey';

/**
 * Per-tab persisted state.
 *
 * Mirrors LocalStorageService but writes to sessionStorage — used for
 * anything that must NOT leak across tabs (masquerade session, workspace
 * pin, etc). sessionStorage is scoped to the tab, so two tabs can hold
 * distinct values under the same key.
 *
 * Try/catch wraps all storage operations so a disabled sessionStorage
 * (Safari private mode, sandboxed iframe) degrades to no-op rather than
 * throwing.
 */
class SessionStorageService {
  private prefix: string;

  constructor(prefix: string) {
    this.prefix = prefix;
  }

  private getPrefixedKey(key: LocalStorageKey): string {
    return `${this.prefix}_${key}`;
  }

  setItem<T>(key: LocalStorageKey, value: T): void {
    const prefixedKey = this.getPrefixedKey(key);
    try {
      if (value === undefined || value === null) {
        sessionStorage.removeItem(prefixedKey);
        return;
      }
      sessionStorage.setItem(prefixedKey, JSON.stringify(value));
    } catch {
      // sessionStorage disabled — silent no-op.
    }
  }

  getItem<T>(key: LocalStorageKey): T | null {
    const prefixedKey = this.getPrefixedKey(key);
    try {
      const raw = sessionStorage.getItem(prefixedKey);
      if (!raw || raw === 'undefined') return null;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return raw as unknown as T;
      }
    } catch {
      return null;
    }
  }

  removeItem(key: LocalStorageKey): void {
    const prefixedKey = this.getPrefixedKey(key);
    try {
      sessionStorage.removeItem(prefixedKey);
    } catch {
      // no-op
    }
  }

  clear(): void {
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(this.prefix)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // no-op
    }
  }
}

export default new SessionStorageService(LocalStorageKey.APP_STORAGE_PREFIX);
